import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Building, Hammer, HardHat, Home, Mail, Thermometer, Wrench } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { z } from 'zod';
import {
  BUSINESS_TYPES,
  BUSINESS_TYPE_LABELS,
  ROLE_LABELS,
  TRADE_TEMPLATES,
  TRADE_TEMPLATE_LABELS,
  businessType as businessTypeSchema,
  tradeTemplate as tradeTemplateSchema,
  type BusinessType,
  type TradeTemplate,
} from '@buildbee/contracts';
import {
  Alert,
  AuthLayout,
  Button,
  ErrorState,
  Field,
  Form,
  FormError,
  FormField,
  Input,
  LoadingState,
  Select,
  Separator,
  cn,
  useZodForm,
} from '@buildbee/ui';
import { billing, canadianSalesTax, DEFAULT_COUNTRY, formatTaxRate, regionLabel, regionsFor, SUPPORTED_COUNTRIES, workspace as workspaceDomain } from '@buildbee/domain';
import { authClient } from '../../../lib/auth-client';
import { errorMessage, trpc } from '../../../lib/trpc';
import { setWorkspaceSlug } from '../../../lib/workspace';
import { SESSION_QUERY_KEY } from '../../../router/parents';
import { brandStyle, CompanyAuthLogo, type CompanyBrand } from '../../../shell/brand';
import { Wordmark } from '../../../shell/Logo';
import { clearPendingWorkspace, loadPendingWorkspace, savePendingWorkspace, VerifyEmailNotice } from './verify';

const BUSINESS_TYPE_META: Record<BusinessType, { icon: ReactNode; blurb: string }> = {
  residential: { icon: <Home />, blurb: 'New homes, homeowner leads and selections' },
  remodeler: { icon: <Hammer />, blurb: 'Kitchens, baths, additions and service work' },
  commercial: { icon: <Building />, blurb: 'Bid invites, RFPs, submittals and owner billing' },
  specialty_sub: { icon: <Wrench />, blurb: 'Bids to GCs, trade packages and lien waivers' },
};

const TRADE_TEMPLATE_ICONS: Record<TradeTemplate, ReactNode> = {
  hvac_plumbing: <Thermometer />,
  remodeling_roofing: <HardHat />,
};

function safeRedirect(target: string | undefined): string {
  return target && target.startsWith('/') && !target.startsWith('//') ? target : '/';
}

/** Logo for an auth page: the company's (company sign-in at /c/<slug>) or BuildBee's. */
function authLogo(company: CompanyBrand | undefined) {
  return company ? <CompanyAuthLogo brand={company} /> : <Wordmark />;
}

/** Re-skins the page with the company colour; renders children unchanged without a company. */
function Branded({ company, children }: { company?: CompanyBrand; children: ReactNode }) {
  if (!company) return <>{children}</>;
  return (
    <div style={brandStyle(company.primaryColor)} data-company={company.slug}>
      {children}
    </div>
  );
}

/**
 * Where email links and social sign-in come back to. On a company sign-in this is the company
 * page (`/c/<slug>?redirect=...`), so the workspace is picked on whichever device opens the link
 * rather than from this browser's storage.
 */
export function authCallbackURL(origin: string, target: string, company?: Pick<CompanyBrand, 'slug'>): string {
  if (!company) return `${origin}${target}`;
  const base = `${origin}/c/${encodeURIComponent(company.slug)}`;
  return target === '/' ? base : `${base}?redirect=${encodeURIComponent(target)}`;
}

/** Header the API reads the typed company code from (apps/api/src/modules/workspace/company-code.ts). */
const COMPANY_CODE_HEADER = 'x-company-code';
/** Same wording as the API, so a wrong code and a wrong password look alike. */
const INVALID_SIGN_IN_MESSAGE = 'Email, password or company code is incorrect';

/** Where a sign-in with a company code lands: /continue resolves the code to a workspace or portal. */
export function continueURL(origin: string, code: string, target: string): string {
  const search = new URLSearchParams({ code });
  if (target !== '/') search.set('redirect', target);
  return `${origin}/continue?${search.toString()}`;
}

/**
 * Company code (letters and numbers), required for everyone on a company's team; locked when the
 * page belongs to one company. "No company code?" is for BuildBee staff and new accounts that have
 * not created a company yet: the API refuses it for anyone on a team.
 */
function CompanyCodeField({
  value,
  onChange,
  locked,
  error,
  onSkip,
}: {
  value: string;
  onChange: (v: string) => void;
  locked: boolean;
  error: string | null;
  onSkip: () => void;
}) {
  return (
    <Field
      label="Company code"
      htmlFor="sign-in-company-code"
      required
      error={error ?? undefined}
      description={
        locked ? undefined : (
          <>
            Ask your company for it (for example VANTOM).{' '}
            <button type="button" className="underline-offset-4 hover:text-foreground hover:underline" onClick={onSkip}>
              No company code? (BuildBee staff and new accounts)
            </button>
          </>
        )
      }
    >
      <Input
        id="sign-in-company-code"
        value={value}
        readOnly={locked}
        aria-invalid={error ? true : undefined}
        autoComplete="organization"
        autoCapitalize="characters"
        spellCheck={false}
        maxLength={16}
        placeholder="e.g. VANTOM"
        className="uppercase"
        onChange={(e) => onChange(e.target.value.toUpperCase())}
      />
    </Field>
  );
}

function SocialButtons({ callbackURL, disabled }: { callbackURL: string; disabled?: boolean }) {
  const [pending, setPending] = useState<'google' | 'microsoft' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const go = async (provider: 'google' | 'microsoft') => {
    setPending(provider);
    setError(null);
    const res = await authClient.signIn.social({ provider, callbackURL });
    if (res.error) {
      setError(res.error.message ?? `${provider} sign-in is not available`);
      setPending(null);
    }
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" disabled={disabled} loading={pending === 'google'} onClick={() => void go('google')}>
          Google
        </Button>
        <Button variant="outline" disabled={disabled} loading={pending === 'microsoft'} onClick={() => void go('microsoft')}>
          Microsoft
        </Button>
      </div>
      {disabled ? <p className="text-xs text-muted-foreground">Enter your company code below to use Google or Microsoft.</p> : null}
      <FormError message={error} />
    </div>
  );
}

function OrDivider() {
  return (
    <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
      <Separator className="flex-1" /> or <Separator className="flex-1" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------

const signInSchema = z.object({ email: z.email('Enter a valid email'), password: z.string().min(1, 'Enter your password') });

export function SignInPage({ redirect, company }: { redirect?: string; company?: CompanyBrand }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'password' | 'magic'>('password');
  const target = safeRedirect(redirect);
  const form = useZodForm(signInSchema, { defaultValues: { email: '', password: '' } });
  const magicForm = useZodForm(z.object({ email: z.email('Enter a valid email') }), { defaultValues: { email: '' } });
  const lockedCode = company?.companyCode ?? null;
  const [typedCode, setTypedCode] = useState('');
  // Signing in without a code: BuildBee staff and new accounts only (the API enforces it).
  const [noCode, setNoCode] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const code = noCode ? '' : (lockedCode ?? typedCode).replace(/[\s-]+/g, '').toUpperCase();
  const codeMissing = !code && !noCode;
  const checkCode = () => {
    setCodeError(codeMissing ? 'Enter your company code' : null);
    return !codeMissing;
  };
  const codeField = noCode ? (
    <p className="text-sm text-muted-foreground">
      Signing in without a company code.{' '}
      <button type="button" className="font-medium text-foreground underline-offset-4 hover:underline" onClick={() => setNoCode(false)}>
        Use a company code
      </button>
    </p>
  ) : (
    <CompanyCodeField
      value={lockedCode ?? typedCode}
      onChange={(v) => {
        setTypedCode(v);
        setCodeError(null);
      }}
      locked={Boolean(lockedCode)}
      error={codeError}
      onSkip={() => {
        setNoCode(true);
        setCodeError(null);
      }}
    />
  );
  const callbackURL = code ? continueURL(window.location.origin, code, target) : authCallbackURL(window.location.origin, target, company);
  const codeHeaders = code ? { headers: { [COMPANY_CODE_HEADER]: code } } : undefined;

  const onPassword = async (values: z.infer<typeof signInSchema>) => {
    setError(null);
    if (!checkCode()) return;
    const res = await authClient.signIn.email({ email: values.email, password: values.password, callbackURL }, codeHeaders);
    if (res.error) {
      // Unverified password account: Better Auth has just re-sent the verification link.
      if (isEmailNotVerified(res.error)) return navigate({ to: '/check-email', search: { email: values.email, reason: 'verify' } });
      if (res.error.status === 429) return setError('Too many attempts. Please wait a few seconds and try again.');
      if (res.error.status === 401) return setError(INVALID_SIGN_IN_MESSAGE);
      return setError(res.error.message ?? 'Invalid email or password');
    }
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    // With a code, /continue opens that company (its workspace, or its portal for a client).
    if (code) return navigate({ to: '/continue', search: { code, redirect: target === '/' ? undefined : target } });
    if (company) setWorkspaceSlug(company.slug);
    await navigate({ to: target });
  };

  const onMagic = async ({ email }: { email: string }) => {
    setError(null);
    if (!checkCode()) return;
    const res = await authClient.signIn.magicLink({ email, callbackURL }, codeHeaders);
    if (res.error) return setError(res.error.message ?? 'Could not send the sign-in link');
    if (company) await navigate({ to: '/c/$slug/check-email', params: { slug: company.slug }, search: { email } });
    else await navigate({ to: '/check-email', search: { email } });
  };

  return (
    <Branded company={company}>
      <AuthLayout
        logo={authLogo(company)}
        title={company ? `Sign in to ${company.name}` : 'Sign in to BuildBee'}
        footer={
          company ? (
            <div className="space-y-2">
              <p>Not on the team yet? Ask {company.name} to invite you.</p>
              <p>
                Are you a client?{' '}
                <Link to="/portal/$slug/sign-in" params={{ slug: company.slug }} className="font-medium text-foreground underline-offset-4 hover:underline">
                  Go to your client portal
                </Link>
              </p>
            </div>
          ) : (
            <>
              New to BuildBee?{' '}
              <Link to="/sign-up" className="font-medium text-foreground underline-offset-4 hover:underline">
                Create an account
              </Link>
            </>
          )
        }
      >
      <div className="space-y-5">
        <SocialButtons callbackURL={callbackURL} disabled={codeMissing} />
        <OrDivider />
        {mode === 'password' ? (
          <Form form={form} onSubmit={onPassword}>
            <FormField name="email" label="Email">
              <Input type="email" autoComplete="email" autoFocus />
            </FormField>
            <FormField name="password" label="Password">
              <Input type="password" autoComplete="current-password" />
            </FormField>
            {codeField}
            <FormError message={error} />
            <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
              Sign in
            </Button>
            <div className="flex justify-between text-sm">
              <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setMode('magic')}>
                Email me a link instead
              </button>
              {company ? (
                <Link to="/c/$slug/forgot-password" params={{ slug: company.slug }} className="text-muted-foreground hover:text-foreground">
                  Forgot password?
                </Link>
              ) : (
                <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground">
                  Forgot password?
                </Link>
              )}
            </div>
          </Form>
        ) : (
          <Form form={magicForm} onSubmit={onMagic}>
            <FormField name="email" label="Email" description="We will email you a one-time sign-in link.">
              <Input type="email" autoComplete="email" autoFocus />
            </FormField>
            {codeField}
            <FormError message={error} />
            <Button type="submit" className="w-full" loading={magicForm.formState.isSubmitting}>
              <Mail /> Send sign-in link
            </Button>
            <button type="button" className="text-sm text-muted-foreground hover:text-foreground" onClick={() => setMode('password')}>
              Use a password instead
            </button>
          </Form>
        )}
      </div>
      </AuthLayout>
    </Branded>
  );
}

// ---------------------------------------------------------------------------
// /continue: open the company a sign-in named by its code
// ---------------------------------------------------------------------------

/**
 * After a sign-in with a company code (password, email link or Google/Microsoft): the team goes to
 * that company's workspace, a client to its portal. A code the account has no access to is
 * refused here too (links and social sign-in are not checked at sign-in time).
 */
export function ContinuePage({ code, redirect }: { code: string; redirect?: string }) {
  const navigate = useNavigate();
  const access = trpc.workspace.me.companyAccess.useQuery({ code }, { retry: false });
  const target = safeRedirect(redirect);

  useEffect(() => {
    if (!access.data) return;
    if (access.data.kind === 'client') {
      void navigate({ to: '/portal/$slug', params: { slug: access.data.slug }, replace: true });
      return;
    }
    setWorkspaceSlug(access.data.slug);
    void navigate({ to: target, replace: true });
  }, [access.data, navigate, target]);

  if (!access.error) return <LoadingState className="min-h-dvh" label="Opening your company" />;
  return (
    <AuthLayout logo={<Wordmark />} title="Can't open that company" description={errorMessage(access.error)}>
      <div className="space-y-3">
        <Button className="w-full" onClick={() => void navigate({ to: '/', replace: true })}>
          Continue to my account
        </Button>
        <Button variant="outline" className="w-full" onClick={() => void authClient.signOut().then(() => navigate({ to: '/sign-in', replace: true }))}>
          Sign in with another account
        </Button>
      </div>
    </AuthLayout>
  );
}

/** Better Auth answers 403 EMAIL_NOT_VERIFIED to a correct password on an unverified account. */
export function isEmailNotVerified(error: { status?: number; code?: string; message?: string }): boolean {
  return error.status === 403 && (error.code === 'EMAIL_NOT_VERIFIED' || /not verified/i.test(error.message ?? ''));
}

/**
 * "Check your email": after a magic-link request (`reason` unset), after a password sign-up
 * (`reason=signup`) or when an unverified account tries to sign in (`reason=verify`).
 */
export function CheckEmailPage({ email, reason, company }: { email?: string; reason?: 'signup' | 'verify'; company?: CompanyBrand }) {
  if (reason) return <VerifyEmailNotice email={email} resent={reason === 'verify'} />;
  return (
    <Branded company={company}>
    <AuthLayout logo={authLogo(company)} title="Check your email" description={email ? `We sent a sign-in link to ${email}.` : 'We sent you a sign-in link.'}>
      <div className="space-y-4 text-center text-sm text-muted-foreground">
        <Mail className="mx-auto size-10" />
        <p>The link expires in 15 minutes and works once. You can close this tab.</p>
        <Button asChild variant="outline">
          {company ? (
            <Link to="/c/$slug/sign-in" params={{ slug: company.slug }}>
              Back to sign in
            </Link>
          ) : (
            <Link to="/sign-in">Back to sign in</Link>
          )}
        </Button>
      </div>
    </AuthLayout>
    </Branded>
  );
}

// ---------------------------------------------------------------------------
// Sign up (account + workspace with business type)
// ---------------------------------------------------------------------------

const signUpForm = z.object({
  name: z.string().trim().min(1, 'Enter your name').max(120),
  email: z.email('Enter a valid email'),
  password: z.string().min(10, 'At least 10 characters').max(128),
  workspaceName: z.string().trim().max(120).optional(),
  businessType: businessTypeSchema.optional(),
  trade: tradeTemplateSchema.optional(),
  country: z.enum(['CA', 'US']),
  region: z.string().trim().max(60).optional(),
});

type Country = 'CA' | 'US';

/** The tax line under the province: "HST 13% will be the default tax" (Canada only). */
export function defaultTaxHint(country: string | undefined, region: string | undefined): string | null {
  if (!region) return null;
  if (country !== 'CA') return 'US sales tax is not set automatically: add your rate in Settings after sign-up.';
  const tax = canadianSalesTax(region);
  if (!tax) return null;
  const label = tax.components.map((c) => c.label).join(' + ');
  return tax.components.length > 1 ? `${label} (${formatTaxRate(tax.totalRateBps)}) will be the default tax` : `${label} will be the default tax`;
}

/** Where the business is: country (Canada by default) and province/state, with the tax it sets. */
export function BusinessLocationFields({
  country,
  region,
  onChange,
  error,
}: {
  country: Country;
  region: string | undefined;
  onChange: (v: { country: Country; region: string }) => void;
  error?: string;
}) {
  const hint = defaultTaxHint(country, region);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Country" htmlFor="ws-country">
        <Select id="ws-country" value={country} onChange={(e) => onChange({ country: e.target.value as Country, region: '' })}>
          {SUPPORTED_COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={regionLabel(country)} htmlFor="ws-region" error={error}>
        <Select id="ws-region" value={region ?? ''} onChange={(e) => onChange({ country, region: e.target.value })}>
          <option value="">Choose…</option>
          {regionsFor(country).map((r) => (
            <option key={r.code} value={r.code}>
              {r.name}
            </option>
          ))}
        </Select>
      </Field>
      {hint ? (
        <p className="text-xs text-muted-foreground sm:col-span-2" data-testid="tax-hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** `{ country, region }` for workspace.create (region omitted when not chosen). */
function locationInput(v: { country?: Country; region?: string }): { country: Country; region?: string } {
  return { country: v.country ?? (DEFAULT_COUNTRY as Country), ...(v.region ? { region: v.region } : {}) };
}

/** `{ trade }` for workspace.create when a trade template was picked. */
function tradeInput(v: { trade?: TradeTemplate }): { trade?: TradeTemplate } {
  return v.trade ? { trade: v.trade } : {};
}

export type WorkTypeChoice = { businessType: BusinessType; trade?: TradeTemplate };

/** One card per business type, then one per trade template (which also picks its business type). */
const WORK_TYPE_OPTIONS: (WorkTypeChoice & { key: string; label: string; icon: ReactNode; blurb: string })[] = [
  ...BUSINESS_TYPES.map((bt) => ({ key: bt, businessType: bt, label: BUSINESS_TYPE_LABELS[bt], ...BUSINESS_TYPE_META[bt] })),
  ...TRADE_TEMPLATES.map((t) => {
    const seed = workspaceDomain.tradeTemplateSeed(t);
    return { key: t, businessType: seed.businessType, trade: t, label: TRADE_TEMPLATE_LABELS[t], icon: TRADE_TEMPLATE_ICONS[t], blurb: seed.blurb };
  }),
];

export function BusinessTypePicker({ value, onChange }: { value?: WorkTypeChoice; onChange: (v: WorkTypeChoice) => void }) {
  const selectedKey = value?.trade ?? value?.businessType;
  return (
    <div role="radiogroup" aria-label="Business type" className="grid gap-2 sm:grid-cols-2">
      {WORK_TYPE_OPTIONS.map((o) => (
        <button
          key={o.key}
          type="button"
          role="radio"
          aria-checked={selectedKey === o.key}
          onClick={() => onChange({ businessType: o.businessType, trade: o.trade })}
          className={cn(
            'flex items-start gap-3 rounded-lg bg-muted/60 p-3 text-left transition-colors hover:bg-muted [&_svg]:size-5',
            selectedKey === o.key && 'bg-accent ring-1 ring-primary hover:bg-accent',
          )}
        >
          <span className="mt-0.5 text-muted-foreground">{o.icon}</span>
          <span>
            <span className="block text-sm font-medium">{o.label}</span>
            <span className="block text-xs text-muted-foreground">{o.blurb}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

export function SignUpPage({ invite }: { invite?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createWorkspace = trpc.workspace.create.useMutation();
  const inviteSignUp = trpc.workspace.invitations.signUp.useMutation();
  const invitation = trpc.workspace.invitations.lookup.useQuery({ token: invite ?? '' }, { enabled: Boolean(invite), retry: false });
  const [error, setError] = useState<string | null>(null);
  const form = useZodForm(signUpForm, {
    defaultValues: { name: '', email: '', password: '', workspaceName: '', businessType: undefined, trade: undefined, country: DEFAULT_COUNTRY as Country, region: '' },
  });
  const businessType = form.watch('businessType');
  const trade = form.watch('trade');
  const country = form.watch('country');
  const region = form.watch('region');
  const invitedEmail = invitation.data?.email;

  // An invitation fixes the address: the account is created for the invited email.
  useEffect(() => {
    if (invitedEmail) form.setValue('email', invitedEmail, { shouldValidate: false });
  }, [invitedEmail, form]);

  const onSubmit = async (values: z.infer<typeof signUpForm>) => {
    setError(null);
    if (invite) {
      // The invitation link proves the address, so the account is verified and signed in at once.
      try {
        const joined = await inviteSignUp.mutateAsync({ token: invite, name: values.name, password: values.password });
        setWorkspaceSlug(joined.slug);
        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
        await navigate({ to: '/' });
      } catch (err) {
        setError(errorMessage(err));
      }
      return;
    }
    if (!values.workspaceName) return form.setError('workspaceName', { message: 'Enter your company name' });
    if (!values.businessType) return form.setError('businessType', { message: 'Choose your business type' });
    const res = await authClient.signUp.email({
      name: values.name,
      email: values.email,
      password: values.password,
      callbackURL: `${window.location.origin}/onboarding`,
    });
    if (res.error) {
      if (res.error.status === 429) return setError('Too many attempts. Please wait a few seconds and try again.');
      return setError(res.error.message ?? 'Could not create your account');
    }
    if (res.data?.token) {
      // Verification not required (should not happen with the default server config): go straight on.
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      try {
        const ws = await createWorkspace.mutateAsync({ name: values.workspaceName, businessType: values.businessType, ...tradeInput(values), ...locationInput(values) });
        setWorkspaceSlug(ws.slug);
        await navigate({ to: '/' });
      } catch (err) {
        setError(errorMessage(err));
      }
      return;
    }
    // The workspace is created after the email is verified (onboarding is pre-filled with these).
    savePendingWorkspace({ name: values.workspaceName, businessType: values.businessType, ...tradeInput(values), ...locationInput(values) });
    await navigate({ to: '/check-email', search: { email: values.email, reason: 'signup' } });
  };

  return (
    <AuthLayout
      logo={<Wordmark />}
      title={invite ? 'Create your account' : 'Start your BuildBee workspace'}
      description={invite ? 'Then you will join the workspace you were invited to.' : `${billing.TRIAL_DAYS}-day free trial of Premium. No credit card required.`}
      footer={
        <>
          Already have an account?{' '}
          <Link to="/sign-in" search={invite ? { redirect: `/invite/${invite}` } : {}} className="font-medium text-foreground underline-offset-4 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <Form form={form} onSubmit={onSubmit}>
        <FormField name="name" label="Your name" required>
          <Input autoComplete="name" autoFocus />
        </FormField>
        <FormField name="email" label="Work email" required description={invite ? 'The address your invitation was sent to.' : undefined}>
          <Input type="email" autoComplete="email" readOnly={Boolean(invite)} />
        </FormField>
        <FormField name="password" label="Password" description="At least 10 characters." required>
          <Input type="password" autoComplete="new-password" />
        </FormField>
        {!invite ? (
          <>
            <FormField name="workspaceName" label="Company name" required>
              <Input autoComplete="organization" placeholder="Summit Builders" />
            </FormField>
            <BusinessLocationFields
              country={country}
              region={region}
              error={form.formState.errors.region?.message}
              onChange={(v) => {
                form.setValue('country', v.country, { shouldDirty: true });
                form.setValue('region', v.region, { shouldDirty: true });
              }}
            />
            <div className="space-y-1.5">
              <p className="text-sm font-medium">
                Business type <span className="text-destructive">*</span>
              </p>
              <BusinessTypePicker
          value={businessType ? { businessType, trade } : undefined}
          onChange={(v) => {
            // A business-type card clears any trade template picked before.
            form.setValue('trade', v.trade);
            form.setValue('businessType', v.businessType, { shouldValidate: true });
          }}
        />
              <p className="text-xs text-muted-foreground">We pre-fill your sales pipeline, project templates and cost codes to match. You can change any of it later.</p>
              {form.formState.errors.businessType ? (
                <p className="text-xs text-destructive">{form.formState.errors.businessType.message}</p>
              ) : null}
            </div>
          </>
        ) : null}
        <FormError message={error} />
        <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
          {invite ? 'Create account and join' : 'Create workspace'}
        </Button>
      </Form>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Onboarding: signed in but no workspace yet (e.g. after Google sign-in)
// ---------------------------------------------------------------------------

const onboardingSchema = z.object({
  name: z.string().trim().min(1, 'Enter your company name').max(120),
  businessType: businessTypeSchema,
  trade: tradeTemplateSchema.optional(),
  country: z.enum(['CA', 'US']),
  region: z.string().trim().max(60).optional(),
});

export function OnboardingPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const create = trpc.workspace.create.useMutation();
  // Company details typed at sign-up (kept in this browser while the email was being verified).
  const [pending] = useState(loadPendingWorkspace);
  // BuildBee staff belong to no workspace: the shell sends them here, so forward them to the console.
  const platform = trpc.platform.me.useQuery();
  useEffect(() => {
    if (platform.data?.platformRole === 'super_admin') void navigate({ to: '/admin' });
  }, [platform.data?.platformRole, navigate]);
  const me = trpc.workspace.me.get.useQuery();
  // A client with shared projects and no company of their own belongs in the portal.
  const portals = trpc.portal.auth.myPortals.useQuery(undefined, { enabled: !pending && me.data?.workspaces.length === 0 });
  useEffect(() => {
    if (portals.data?.length) void navigate({ to: '/portal', replace: true });
  }, [portals.data, navigate]);
  const form = useZodForm(onboardingSchema, {
    defaultValues: { name: pending?.name ?? '', businessType: pending?.businessType, trade: pending?.trade, country: pending?.country ?? (DEFAULT_COUNTRY as Country), region: pending?.region ?? '' },
  });
  const businessType = form.watch('businessType');
  const trade = form.watch('trade');
  const country = form.watch('country');
  const region = form.watch('region');

  const onSubmit = async (values: z.infer<typeof onboardingSchema>) => {
    const ws = await create.mutateAsync({ name: values.name, businessType: values.businessType, ...tradeInput(values), ...locationInput(values) });
    clearPendingWorkspace();
    setWorkspaceSlug(ws.slug);
    await utils.workspace.me.get.invalidate();
    await navigate({ to: '/' });
  };

  // One company per owner: a second one is added by BuildBee support, on its own plan.
  if (me.data && !me.data.canCreateWorkspace) {
    return (
      <AuthLayout logo={<Wordmark />} title="You already have a workspace" description="Each owner can set up one company.">
        <div className="space-y-4">
          <Alert variant="info" title="Need another company?">
            Contact BuildBee support. We&apos;ll add it to your account on its own plan.
          </Alert>
          <Button asChild className="w-full">
            <Link to="/">Back to my workspace</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout logo={<Wordmark />} title="Set up your workspace" description="Each company gets its own private workspace.">
      <Form form={form} onSubmit={onSubmit}>
        <FormField name="name" label="Company name" required>
          <Input autoFocus placeholder="Summit Builders" />
        </FormField>
        <BusinessLocationFields
          country={country}
          region={region}
          error={form.formState.errors.region?.message}
          onChange={(v) => {
            form.setValue('country', v.country, { shouldDirty: true });
            form.setValue('region', v.region, { shouldDirty: true });
          }}
        />
        <BusinessTypePicker
          value={businessType ? { businessType, trade } : undefined}
          onChange={(v) => {
            // A business-type card clears any trade template picked before.
            form.setValue('trade', v.trade);
            form.setValue('businessType', v.businessType, { shouldValidate: true });
          }}
        />
        {form.formState.errors.businessType ? <p className="text-xs text-destructive">Choose your business type</p> : null}
        <FormError message={create.error ? errorMessage(create.error) : null} />
        <Button type="submit" className="w-full" loading={create.isPending}>
          Create workspace
        </Button>
      </Form>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Accept invitation
// ---------------------------------------------------------------------------

export function AcceptInvitePage({ token }: { token: string }) {
  const navigate = useNavigate();
  const session = authClient.useSession();
  const lookup = trpc.workspace.invitations.lookup.useQuery({ token }, { retry: false });
  const accept = trpc.workspace.invitations.accept.useMutation({
    onSuccess: async (res) => {
      setWorkspaceSlug(res.slug);
      await navigate({ to: '/' });
    },
  });

  if (lookup.isLoading || session.isPending) return <LoadingState className="min-h-dvh" label="Checking invitation" />;
  if (lookup.error || !lookup.data) {
    return (
      <AuthLayout logo={<Wordmark />} title="Invitation not found">
        <ErrorState title="This invitation link is invalid" error="Ask the person who invited you to send a new link." />
      </AuthLayout>
    );
  }
  const inv = lookup.data;
  const signedInAs = session.data?.user.email;

  return (
    <AuthLayout
      logo={<Wordmark />}
      title={`Join ${inv.workspaceName}`}
      description={`${inv.inviterName ?? 'A teammate'} invited ${inv.email} as ${ROLE_LABELS[inv.role]}.`}
    >
      {inv.status !== 'pending' ? (
        <Alert variant="warning" title={`This invitation is ${inv.status}`}>
          Ask for a new invitation if you still need access.
        </Alert>
      ) : signedInAs ? (
        <div className="space-y-4">
          {signedInAs.toLowerCase() !== inv.email.toLowerCase() ? (
            <Alert variant="warning" title={`You are signed in as ${signedInAs}`}>
              This invitation is for {inv.email}. Sign out and sign in with that address to accept it.
            </Alert>
          ) : null}
          <FormError message={accept.error ? errorMessage(accept.error) : null} />
          <Button className="w-full" loading={accept.isPending} onClick={() => accept.mutate({ token })}>
            <HardHat /> Accept invitation
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Button asChild className="w-full">
            <Link to="/sign-up" search={{ invite: token }}>
              Create an account
            </Link>
          </Button>
          <Button asChild variant="outline" className="w-full">
            <Link to="/sign-in" search={{ redirect: `/invite/${token}` }}>
              I already have an account
            </Link>
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Password reset
// ---------------------------------------------------------------------------

export function ForgotPasswordPage({ company }: { company?: CompanyBrand } = {}) {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useZodForm(z.object({ email: z.email('Enter a valid email') }), { defaultValues: { email: '' } });
  const onSubmit = async ({ email }: { email: string }) => {
    setError(null);
    const resetPath = company ? `/c/${encodeURIComponent(company.slug)}/reset-password` : '/reset-password';
    const res = await authClient.requestPasswordReset({ email, redirectTo: `${window.location.origin}${resetPath}` });
    if (res.error) return setError(res.error.message ?? 'Could not send reset email');
    setSent(true);
  };
  return (
    <Branded company={company}>
    <AuthLayout
      logo={authLogo(company)}
      title="Reset your password"
      footer={company ? <Link to="/c/$slug/sign-in" params={{ slug: company.slug }}>Back to sign in</Link> : <Link to="/sign-in">Back to sign in</Link>}
    >
      {sent ? (
        <Alert variant="success" title="Check your email">
          If an account exists for that address, a reset link is on its way.
        </Alert>
      ) : (
        <Form form={form} onSubmit={onSubmit}>
          <FormField name="email" label="Email">
            <Input type="email" autoComplete="email" autoFocus />
          </FormField>
          <FormError message={error} />
          <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
            Send reset link
          </Button>
        </Form>
      )}
    </AuthLayout>
    </Branded>
  );
}

export function ResetPasswordPage({ token, company }: { token?: string; company?: CompanyBrand }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const form = useZodForm(z.object({ password: z.string().min(10, 'At least 10 characters').max(128) }), { defaultValues: { password: '' } });
  if (!token) {
    return (
      <Branded company={company}>
        <AuthLayout logo={authLogo(company)} title="Reset link invalid">
          <ErrorState error="This reset link is missing its token. Request a new one." />
        </AuthLayout>
      </Branded>
    );
  }
  const onSubmit = async ({ password }: { password: string }) => {
    setError(null);
    const res = await authClient.resetPassword({ newPassword: password, token });
    if (res.error) return setError(res.error.message ?? 'Could not reset password');
    if (company) await navigate({ to: '/c/$slug/sign-in', params: { slug: company.slug } });
    else await navigate({ to: '/sign-in' });
  };
  return (
    <Branded company={company}>
    <AuthLayout logo={authLogo(company)} title="Choose a new password">
      <Form form={form} onSubmit={onSubmit}>
        <FormField name="password" label="New password">
          <Input type="password" autoComplete="new-password" autoFocus />
        </FormField>
        <FormError message={error} />
        <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
          Update password
        </Button>
      </Form>
    </AuthLayout>
    </Branded>
  );
}
